// /admin → Volunteer Admin Dashboard — everything the operator does ABOUT the
// volunteers' work, in one in-page tool with seven tabs:
//
//   Volunteer access   who may open Volunteer Tools (VolunteerAccess)
//   In progress        issues a volunteer marked In Progress (InProgressIssues)
//   Route ratings      the ratings log, read-only — moved here from the
//                      Dashboard; triage itself is on /volunteer/issues
//   Restaurants        the review queue and the highlights (RestaurantsAdmin)
//   Room names         drafts waiting to be published, and the live set
//                      (/api/room-names?part=admin; publish / unpublish)
//   Content edits      the Content Filler's overlay, one row per edited place
//                      (/api/place-edits?part=admin)
//   Route suggestions  what the Route tester sent (/api/route-tests?part=admin):
//                      three piles, review notes, and "Open in tester"
//
// The first four are the panels /admin already had, mounted unchanged. The
// last three are small tables over endpoints Volunteer Tools built; the admin
// token passes each of their `volunteerAccess` gates, and the publish /
// review / delete ops are operator-only on the server.
//
// `data` is the /api/admin-data payload AdminPage already fetched (the ratings
// tab reads `routeRatings` / `routeRatingCounts` off it, and the spreadsheet
// export is defined once in utils/adminExport.js); `onRefresh` re-fetches it.
// The three small lists are fetched here, on mount, so every tab's count is
// on the tab bar before it is opened. Each panel keeps its own `S` copy — this
// one is the keys it needs from AdminPage's, so the look is the same.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import VolunteerAccess from './VolunteerAccess.jsx';
import InProgressIssues from './InProgressIssues.jsx';
import RestaurantsAdmin from './RestaurantsAdmin.js';
import { fmtTime, optionTags, destLabel, triageStatus, TRIAGE_LABELS } from '../utils/adminLabels.js';
import {
  canExport, exportRowCount, pageSheetCount, downloadPageXlsx, downloadPageCsv,
} from '../utils/adminExport.js';

const TABS = [
  { id: 'access', label: 'Volunteer access' },
  { id: 'progress', label: 'In progress' },
  { id: 'ratings', label: 'Route ratings' },
  { id: 'restaurants', label: 'Restaurants' },
  { id: 'rooms', label: 'Room names' },
  { id: 'content', label: 'Content edits' },
  { id: 'suggestions', label: 'Route suggestions' },
];

/**
 * The live map, reopened on the exact route a rating was left about.
 *
 * ⚠️ THE OPTIONS ARE HALF THE ROUTE. The same From→To with the accessible
 * toggle on is a different path through a different set of shafts, so a link
 * carrying only `b` and `r` would reopen a route nobody complained about.
 * `a` and `t` are the pathfinder's own tri-states. Null when the rating names
 * no destination room (a route to a building's entrance has no room).
 * Same construction as `routeLink` in src/volunteer/shared.js.
 */
export function routeLink(r) {
  const to = r?.to;
  if (!to?.building || !to?.room) return null;
  try {
    const p = new URLSearchParams();
    if (r.from) p.set('from', r.from);
    p.set('b', to.building);
    p.set('r', to.room);
    const o = r.options || {};
    if (o.accessible) p.set('a', '1');
    else if (o.noElevators) p.set('a', '2');
    if (o.tunnels) p.set('t', '1');
    else if (o.noTunnels) p.set('t', '2');
    return `${window.location.origin}/umanitoba?${p.toString()}`;
  } catch { return null; }
}

export default function VolunteerAdmin({
  apiBase, token, onUnauthorized, initialTab, data, onRefresh, inProgressCount = 0,
}) {
  const [tab, setTab] = useState(() => (TABS.some((t) => t.id === initialTab) ? initialTab : 'access'));
  const unauthRef = useRef(onUnauthorized);
  unauthRef.current = onUnauthorized;

  // One fetch helper for the three small endpoints. A 401 is a dead session
  // (or, with ADMIN_PASSWORD unset, a live token that is not the operator's —
  // VolunteerAccess tells the two apart the same way); a 404 with no JSON
  // error is the endpoint not being deployed here, which is the honest
  // message while the server side is still landing.
  const call = useCallback(async (path, body) => {
    const r = await fetch(`${apiBase}${path}`, {
      method: body ? 'POST' : 'GET',
      headers: { Authorization: `Bearer ${token}`, ...(body ? { 'Content-Type': 'application/json' } : {}) },
      ...(body ? { body: JSON.stringify(body) } : {}),
    });
    const j = await r.json().catch(() => ({}));
    if (r.status === 401) {
      if (/operator/i.test(j.error || '')) throw new Error(j.error);
      unauthRef.current?.();
      throw new Error('Session expired — log in again.');
    }
    if (r.status === 404 && !j.error) throw new Error(`${path.split('?')[0]} is not deployed here yet.`);
    if (!r.ok || j.ok === false) throw new Error(j.error || 'That did not go through.');
    return j;
  }, [apiBase, token]);

  // ── The three lists this tool reads itself ─────────────────────────────────
  const [sugg, setSugg] = useState({ list: null, counts: null, err: '' });
  const [rooms, setRooms] = useState({ drafts: null, live: null, err: '' });
  const [edits, setEdits] = useState({ rows: null, err: '' });

  const loadSuggestions = useCallback(async () => {
    try {
      const j = await call('/api/route-tests?part=admin');
      setSugg({ list: Array.isArray(j.suggestions) ? j.suggestions : [], counts: j.counts || null, err: '' });
    } catch (e) { setSugg((s) => ({ ...s, list: s.list || [], err: e.message })); }
  }, [call]);

  const loadRooms = useCallback(async () => {
    try {
      const j = await call('/api/room-names?part=admin');
      setRooms({ drafts: j.drafts || {}, live: j.live || {}, err: '' });
    } catch (e) { setRooms((s) => ({ ...s, drafts: s.drafts || {}, live: s.live || {}, err: e.message })); }
  }, [call]);

  const loadEdits = useCallback(async () => {
    try {
      const j = await call('/api/place-edits?part=admin');
      setEdits({ rows: j.edits && typeof j.edits === 'object' ? j.edits : {}, err: '' });
    } catch (e) { setEdits((s) => ({ ...s, rows: s.rows || {}, err: e.message })); }
  }, [call]);

  useEffect(() => { loadSuggestions(); loadRooms(); loadEdits(); }, [loadSuggestions, loadRooms, loadEdits]);

  const ratings = data?.routeRatings || [];
  const draftCount = rooms.drafts ? Object.keys(rooms.drafts).length : null;
  const editCount = edits.rows ? Object.keys(edits.rows).length : null;
  const pendingCount = sugg.counts ? (Number(sugg.counts.pending) || 0) : null;

  const count = (id) => {
    if (id === 'progress') return inProgressCount;
    if (id === 'ratings') return ratings.length;
    if (id === 'rooms') return draftCount;
    if (id === 'content') return editCount;
    if (id === 'suggestions') return pendingCount;
    return null;
  };
  // A red dot is for work waiting on the operator, not for rows that exist.
  const dot = (id) => (
    (id === 'progress' && inProgressCount > 0)
    || (id === 'rooms' && draftCount > 0)
    || (id === 'suggestions' && pendingCount > 0)
  );

  return (
    <div>
      <div style={S.tabBar}>
        {TABS.map((t) => {
          const n = count(t.id);
          return (
            <button
              key={t.id}
              style={{ ...S.tab, ...(tab === t.id ? S.tabOn : {}) }}
              onClick={() => setTab(t.id)}
            >
              {t.label}{n != null ? ` (${n})` : ''}
              {dot(t.id) && tab !== t.id && <span style={S.tabDot} />}
            </button>
          );
        })}
      </div>

      {tab === 'access' && (
        <VolunteerAccess apiBase={apiBase} token={token} onUnauthorized={onUnauthorized} />
      )}

      {/* The last step of the issues workflow: a volunteer marks a report or a
          bad route In Progress on /volunteer/issues, it lands here, and the
          operator marks it Fixed once the map is changed. */}
      {tab === 'progress' && (
        <InProgressIssues
          apiBase={apiBase}
          token={token}
          routeLink={routeLink}
          onChanged={onRefresh}
          onUnauthorized={onUnauthorized}
        />
      )}

      {tab === 'ratings' && <RatingsTab data={data} onRefresh={onRefresh} />}

      {tab === 'restaurants' && <RestaurantsAdmin token={token} onExpired={onUnauthorized} />}

      {tab === 'rooms' && <RoomNamesTab rooms={rooms} call={call} reload={loadRooms} />}

      {tab === 'content' && <ContentEditsTab edits={edits} reload={loadEdits} />}

      {tab === 'suggestions' && (
        <SuggestionsTab sugg={sugg} call={call} reload={loadSuggestions} apiBase={apiBase} token={token} />
      )}
    </div>
  );
}

// ── Route ratings ───────────────────────────────────────────────────────────
// The Dashboard's ratings tab, moved here as it was. Read here, WORKED there:
// the status dropdown and the dev note live on /volunteer/issues, and a second
// place to set the same status is how the two stop agreeing.

const VERDICT_FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'bad', label: 'Bad' },
  { id: 'ok', label: 'Ok' },
  { id: 'good', label: 'Good' },
];

/** Bad is red, good is green. A verdict IS a value here, unlike the tabs. */
const RATING_TONE = {
  bad: { background: '#fce8e6', color: '#c5221f' },
  ok: { background: '#e8f0fe', color: '#1967d2' },
  good: { background: '#e6f4ea', color: '#137333' },
};

// Amber is in progress, green is done, and "not reviewed" is deliberately the
// plainest of the four rather than a red alarm.
const TRIAGE_TONE = {
  new: { background: '#fff', color: '#5f6368', borderColor: '#dadce0' },
  checking: { background: '#fef7e0', color: '#b06000', borderColor: '#f6d08a' },
  fixed: { background: '#e6f4ea', color: '#137333', borderColor: '#a8dab5' },
  unknown: { background: '#f1f3f4', color: '#3c4043', borderColor: '#dadce0' },
};

function pct(n, total) {
  if (!total) return '—';
  return `${Math.round((n / total) * 100)}%`;
}

function RatingsTab({ data, onRefresh }) {
  // The filter narrows, it does not sort: the list stays newest-first, because
  // a run of bad ratings in one afternoon is a regression and must read as one.
  const [ratingVerdict, setRatingVerdict] = useState('all');
  const ratings = data?.routeRatings || [];
  const ratingCounts = data?.routeRatingCounts || null;
  const visibleRatings = ratingVerdict === 'all' ? ratings : ratings.filter((r) => r.verdict === ratingVerdict);

  return (
    <>
      <ActionBar>
        <ActionGroup label="Triage" note="Statuses and notes for bad ratings are set on /volunteer/issues. Ones marked In Progress come back to the In progress tab here.">
          <a style={S.btnGhost} href="/volunteer/issues">Open Reports &amp; bad routes ↗</a>
          {onRefresh && <button style={S.btnGhost} onClick={onRefresh} disabled={!data}>Refresh</button>}
        </ActionGroup>
        <ExportGroup tab="ratings" data={data} />
      </ActionBar>
      {!data && <div style={S.reportEmpty}>Loading…</div>}
      {ratingCounts && (
        <div style={S.counts}>
          <Stat label="Rated" value={ratingCounts.total} hint="routes rated" />
          <Stat
            label="Bad"
            value={pct(ratingCounts.bad, ratingCounts.total)}
            hint={`${ratingCounts.bad} of ${ratingCounts.total}`}
            tone={ratingCounts.bad > 0 ? 'warn' : undefined}
          />
          <Stat label="Ok" value={pct(ratingCounts.ok, ratingCounts.total)} hint={String(ratingCounts.ok)} />
          <Stat label="Good" value={pct(ratingCounts.good, ratingCounts.total)} hint={String(ratingCounts.good)} />
          <Stat
            label="Accessible · bad"
            value={pct(ratingCounts.accessible.bad, ratingCounts.accessible.total)}
            hint={`${ratingCounts.accessible.bad} of ${ratingCounts.accessible.total} accessible`}
            tone={ratingCounts.accessible.bad > 0 ? 'warn' : undefined}
          />
          <Stat label="With a note" value={ratingCounts.withNote} hint="bad + explained" />
        </div>
      )}
      {data && (
        <div style={S.filterBar}>
          <ActionGroup label="Verdict">
            {VERDICT_FILTERS.map((f) => (
              <button
                key={f.id}
                style={{ ...S.chip, ...(ratingVerdict === f.id ? S.chipOn : {}) }}
                onClick={() => setRatingVerdict(f.id)}
              >
                {f.label} ({f.id === 'all' ? ratings.length : ratings.filter((r) => r.verdict === f.id).length})
              </button>
            ))}
          </ActionGroup>
        </div>
      )}
      {data && (
        <div style={S.reportList}>
          {ratings.length === 0 && <div style={S.reportEmpty}>No ratings yet.</div>}
          {ratings.length > 0 && visibleRatings.length === 0 && (
            <div style={S.reportEmpty}>Nothing matches that filter.</div>
          )}
          {visibleRatings.map((r) => {
            const link = routeLink(r);
            const status = r.verdict === 'bad' ? triageStatus(r) : null;
            return (
              <div key={r.key} style={S.reportItem}>
                {/* ⚠️ THE ROUTE IS NEVER TRUNCATED — the destination is the
                    half that says WHICH route somebody is complaining about. */}
                <div style={{ ...S.reportHead, ...S.ratingHead }}>
                  <span style={{ ...S.ratingChip, ...(RATING_TONE[r.verdict] || {}) }}>
                    {r.verdict}
                  </span>
                  <div style={S.ratingMain}>
                    <div style={S.ratingRoute}>
                      {r.from || '—'}
                      <span style={S.ratingArrow}>→</span>
                      {destLabel(r.to) || '—'}
                    </div>
                    <div style={S.ratingMeta}>
                      {[
                        r.meters != null ? `${r.meters} m` : '',
                        optionTags(r.options) === '—' ? '' : optionTags(r.options),
                        r.device || 'Unknown device',
                        fmtTime(r.ts),
                      ].filter(Boolean).join(' · ')}
                    </div>
                  </div>
                  <div style={S.ratingControls}>
                    {/* The status as a read-only label: set on the volunteer side. */}
                    {status && status !== 'new' && (
                      <span style={{ ...S.statusSelect, ...(TRIAGE_TONE[status] || {}) }}>
                        {TRIAGE_LABELS[status] || status}
                      </span>
                    )}
                    {link ? (
                      <a style={S.openRoute} href={link} target="_blank" rel="noreferrer">Open route ↗</a>
                    ) : (
                      <span style={S.openRouteOff} title="This rating carries no destination room, so there is no route to rebuild.">
                        no route to open
                      </span>
                    )}
                  </div>
                </div>
                {!!r.note && <div style={S.ratingSaid}>“{r.note}”</div>}
                {!!r.triage?.note && (
                  <div style={S.volNote}>
                    <div style={S.noteLabel}>Volunteer note{r.triage.by ? ` · ${r.triage.by}` : ''}</div>
                    <div style={S.volNoteText}>{r.triage.note}</div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}

// ── Room names ──────────────────────────────────────────────────────────────
// A compact table over `GET /api/room-names?part=admin` → `{ drafts, live }`,
// both keyed `<slug>::<room>`. Publishing is the whole step — no deploy — and
// only the operator can do it. RoomNames.jsx's own AdminPanel is not exported
// (and is built on the vt-* stylesheet), so this is its two tables redrawn in
// /admin's look; the "Export for UMapsTools" zip stays on /volunteer/rooms.

function RoomNamesTab({ rooms, call, reload }) {
  const [busy, setBusy] = useState('');
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  const [showLive, setShowLive] = useState(false);

  const drafts = useMemo(() => sortRooms(rooms.drafts, 'updatedAt'), [rooms.drafts]);
  const live = useMemo(() => sortRooms(rooms.live, 'publishedAt'), [rooms.live]);

  const post = async (body, label) => {
    setBusy(label); setMsg(''); setErr('');
    try {
      const out = await call('/api/room-names', body);
      await reload();
      return out;
    } catch (e) { setErr(e.message); return null; }
    finally { setBusy(''); }
  };
  const publish = async (ids) => {
    const n = ids ? ids.length : drafts.length;
    if (!n) return;
    if (!window.confirm(`Publish ${n} name${n === 1 ? '' : 's'}? They show on the website within a minute and in the app on its next launch.`)) return;
    const out = await post({ op: 'publish', ...(ids ? { ids } : {}) }, ids ? `pub:${ids.join()}` : 'publish');
    if (out) setMsg(`Published ${out.published}.`);
  };
  const unpublish = async (ids) => {
    const out = await post({ op: 'unpublish', ids }, `un:${ids.join()}`);
    if (out) setMsg(`Took ${out.removed} off the map; the sheet name stands.`);
  };
  const drop = async (d) => {
    if (!window.confirm(`Drop the draft "${d.name || '(blank)'}" for ${d.building} ${d.roomNumber}?`)) return;
    const out = await post({ op: 'revert', building: d.building, roomNumber: d.roomNumber }, `rv:${d.id}`);
    if (out) setMsg('Draft dropped.');
  };

  return (
    <>
      <ActionBar>
        <ActionGroup
          label="Publish"
          note="Publishing puts the names on the website and in the app — no deploy. The export that brings the authored sheets in UMapsTools up to date is on /volunteer/rooms."
        >
          <button style={S.noteSave} disabled={!drafts.length || !!busy} onClick={() => publish()}>
            {busy === 'publish' ? 'Publishing…' : `Publish all drafts (${drafts.length})`}
          </button>
          <button style={S.btnGhost} onClick={() => setShowLive((v) => !v)} disabled={!live.length}>
            {showLive ? 'Hide live names' : `Live names (${live.length})`}
          </button>
          <a style={S.btnGhost} href="/volunteer/rooms">Open Room names ↗</a>
          <button style={S.btnGhost} onClick={reload} disabled={!!busy}>Refresh</button>
        </ActionGroup>
      </ActionBar>
      {rooms.err && <div style={S.err}>{rooms.err}</div>}
      {err && <div style={S.err}>{err}</div>}
      {msg && <div style={S.ok}>{msg}</div>}

      <div style={S.sectionTitle}>Drafts waiting ({drafts.length})</div>
      <div style={S.tableWrap}>
        <table style={S.table}>
          <thead><tr>
            <th style={S.th}>Building</th><th style={S.th}>Room</th><th style={S.th}>Name</th>
            <th style={S.th}>By</th><th style={S.th}>Saved</th><th style={S.th}></th>
          </tr></thead>
          <tbody>
            {rooms.drafts === null && <tr><td style={S.td} colSpan={6}>Loading…</td></tr>}
            {rooms.drafts !== null && drafts.length === 0 && (
              <tr><td style={S.td} colSpan={6}>No drafts waiting. Names volunteers save on /volunteer/rooms appear here.</td></tr>
            )}
            {drafts.map((d) => (
              <tr key={d.id}>
                <td style={S.td}><span style={S.mono}>{d.building}</span></td>
                <td style={S.td}>{d.roomNumber}</td>
                <td style={S.td}>{d.name || <i style={S.dim}>(blank — hides the sheet name)</i>}</td>
                <td style={S.td}>{d.by || <span style={S.dim}>—</span>}</td>
                <td style={S.td}>{fmtTime(d.updatedAt)}</td>
                <td style={{ ...S.td, whiteSpace: 'nowrap' }}>
                  <button style={S.smallPrimary} disabled={!!busy} onClick={() => publish([d.id])}>Publish</button>
                  <button style={{ ...S.smallGhost, marginLeft: 6 }} disabled={!!busy} onClick={() => drop(d)}>Drop</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showLive && (
        <>
          <div style={S.sectionTitle}>Live names ({live.length})</div>
          <div style={S.tableWrap}>
            <table style={S.table}>
              <thead><tr>
                <th style={S.th}>Building</th><th style={S.th}>Room</th><th style={S.th}>Name</th>
                <th style={S.th}>By</th><th style={S.th}>Published</th><th style={S.th}></th>
              </tr></thead>
              <tbody>
                {live.length === 0 && <tr><td style={S.td} colSpan={6}>Nothing published from here yet.</td></tr>}
                {live.map((d) => (
                  <tr key={d.id}>
                    <td style={S.td}><span style={S.mono}>{d.building}</span></td>
                    <td style={S.td}>{d.roomNumber}</td>
                    <td style={S.td}>{d.name || <i style={S.dim}>(blank — hides the sheet name)</i>}</td>
                    <td style={S.td}>{[d.by, d.publishedBy && d.publishedBy !== d.by ? `published by ${d.publishedBy}` : ''].filter(Boolean).join(' · ') || <span style={S.dim}>—</span>}</td>
                    <td style={S.td}>{fmtTime(d.publishedAt)}</td>
                    <td style={{ ...S.td, whiteSpace: 'nowrap' }}>
                      <button style={S.smallGhost} disabled={!!busy} onClick={() => unpublish([d.id])}>Unpublish</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </>
  );
}

/** A keyed set → rows, newest first. */
function sortRooms(set, stampField) {
  if (!set) return [];
  return Object.entries(set)
    .filter(([, e]) => e && typeof e === 'object')
    .map(([id, e]) => ({ id, ...e }))
    .sort((a, b) => String(b[stampField] || '').localeCompare(String(a[stampField] || '')));
}

// ── Content edits ───────────────────────────────────────────────────────────
// `GET /api/place-edits?part=admin` → `{ edits: { [placeId]: overlay } }`, the
// Content Filler's field-level overlay (api/_placeEdits.js). One row per
// edited place, saying WHICH fields were touched — the values themselves are
// on /volunteer/content, beside the authored ones they override.

const PLACE_TYPES = { study: 'Study spot', library: 'Library', amenity: 'Amenity', restaurant: 'Restaurant' };

function changedFields(e) {
  const out = Object.keys(e?.fields || {});
  const m = e?.menu || {};
  const patched = Object.keys(m.items || {}).length;
  const added = Array.isArray(m.added) ? m.added.length : 0;
  const removed = Array.isArray(m.removed) ? m.removed.length : 0;
  const menu = [
    patched ? `${patched} item${patched === 1 ? '' : 's'} changed` : '',
    added ? `${added} added` : '',
    removed ? `${removed} hidden` : '',
  ].filter(Boolean).join(', ');
  if (menu) out.push(`menu (${menu})`);
  const hidden = Array.isArray(e?.hiddenImages) ? e.hiddenImages.length : 0;
  if (hidden) out.push(`${hidden} photo${hidden === 1 ? '' : 's'} hidden`);
  return out;
}

function ContentEditsTab({ edits, reload }) {
  const rows = useMemo(() => Object.entries(edits.rows || {})
    .filter(([, e]) => e && typeof e === 'object')
    .map(([id, e]) => ({ id, ...e, changed: changedFields(e) }))
    .sort((a, b) => String(b.updatedAt || '').localeCompare(String(a.updatedAt || ''))), [edits.rows]);

  return (
    <>
      <ActionBar>
        <ActionGroup
          label="Content Filler"
          note="Every place a volunteer has changed. Each row lists the fields that override the authored file; the values, and the Reset per field, are on the volunteer side."
        >
          <a style={S.btnGhost} href="/volunteer/content">Open Content Filler ↗</a>
          <button style={S.btnGhost} onClick={reload}>Refresh</button>
        </ActionGroup>
      </ActionBar>
      {edits.err && <div style={S.err}>{edits.err}</div>}
      <div style={S.tableWrap}>
        <table style={S.table}>
          <thead><tr>
            <th style={S.th}>Place</th><th style={S.th}>Type</th><th style={S.th}>Changed</th>
            <th style={S.th}>By</th><th style={S.th}>Updated</th><th style={S.th}></th>
          </tr></thead>
          <tbody>
            {edits.rows === null && <tr><td style={S.td} colSpan={6}>Loading…</td></tr>}
            {edits.rows !== null && rows.length === 0 && (
              <tr><td style={S.td} colSpan={6}>No place has been edited yet.</td></tr>
            )}
            {rows.map((r) => (
              <tr key={r.id}>
                <td style={S.td}>
                  {r.fields?.name ? <div style={{ fontWeight: 600 }}>{r.fields.name}</div> : null}
                  <span style={S.mono}>{r.id}</span>
                </td>
                <td style={S.td}>{PLACE_TYPES[r.type] || r.type || <span style={S.dim}>—</span>}</td>
                <td style={S.td}>{r.changed.length ? r.changed.join(', ') : <span style={S.dim}>nothing</span>}</td>
                <td style={S.td}>{r.by || <span style={S.dim}>—</span>}</td>
                <td style={S.td}>{fmtTime(r.updatedAt)}</td>
                <td style={S.td}><a style={S.openRoute} href="/volunteer/content">Open ↗</a></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

// ── Route suggestions ───────────────────────────────────────────────────────
// What the Route tester (/volunteer/routes) sent: a volunteer's original
// Start → End, the graph edits they tried, the before/after numbers, a
// comment and maybe a screenshot. The edits NEVER touched map data; a
// suggestion is a request for somebody to change it in UMapsTools. The
// operator answers with a status and a note (`op: 'review'`), which the
// volunteer reads back on their "My suggestions" list.

const VARIANT_LABELS = { base: 'Normal', stairs: 'Stairs only', accessible: 'Accessible', weather: 'Bad weather (tunnels)' };
const VARIANT_ORDER = ['base', 'stairs', 'accessible', 'weather'];

const PILES = [
  { id: 'pending', label: 'Pending', countKey: 'pending' },
  { id: 'needs-detail', label: 'Needs detail', countKey: 'needsDetail' },
  { id: 'closed', label: 'Closed', countKey: 'closed' },
];

const STATUS_TONE = {
  pending: { background: '#e8f0fe', color: '#1967d2' },
  'needs-detail': { background: '#fef7e0', color: '#b06000' },
  closed: { background: '#e6f4ea', color: '#137333' },
};
const STATUS_LABEL = { pending: 'pending', 'needs-detail': 'needs detail', closed: 'closed' };

/** An Endpoint, as the volunteer would say it. */
function endpointText(ep) {
  if (!ep) return '—';
  if (ep.label) return ep.label;
  if (ep.kind === 'room') return `${ep.building || ''} ${ep.room || ''}`.trim() || '—';
  return ep.id || '—';
}

/** A graph edit, in words: what became what, and where. */
export function editText(e) {
  if (!e || typeof e !== 'object') return '';
  const where = [
    e.floor != null && e.floor !== '' ? `on floor ${e.floor}` : '',
    e.building ? (e.floor != null && e.floor !== '' ? `of ${e.building}` : `in ${e.building}`) : '',
  ].filter(Boolean).join(' ');
  if (e.kind === 'shaft') {
    const ids = Array.isArray(e.nodeIds) ? e.nodeIds : [];
    const name = e.label || ids[0] || '';
    const n = ids.length;
    return [
      `Disabled ${e.type === 'elevator' ? 'elevator' : e.type === 'stair' ? 'stair' : 'shaft'}`,
      name, n ? `(${n} floor${n === 1 ? '' : 's'})` : '', e.building ? `in ${e.building}` : '',
    ].filter(Boolean).join(' ');
  }
  const name = e.label || e.id || '';
  if (e.type === 'blocked') return ['Blocked node', name, where].filter(Boolean).join(' ');
  if (e.type === 'non-accessible') return ['Marked node', name, 'non-accessible', where].filter(Boolean).join(' ');
  return [e.kind, e.type, name, where].filter(Boolean).join(' ');
}

/** One variant's numbers as a phrase; null is "No route", absent is "—". */
function metricText(m) {
  if (m === null) return 'No route';
  if (!m || typeof m !== 'object') return '—';
  const fc = Number(m.floorChanges) || 0;
  return `${Math.round(Number(m.meters) || 0)} m · ${fc} floor change${fc === 1 ? '' : 's'} · ${Number(m.mins) || 0} min`;
}

function variantsText(v) {
  const on = ['base', ...VARIANT_ORDER.slice(1).filter((k) => v && v[k])];
  return on.map((k) => VARIANT_LABELS[k]).join(', ');
}

function SuggestionsTab({ sugg, call, reload, apiBase, token }) {
  const [pile, setPile] = useState('pending');
  const [busy, setBusy] = useState('');
  const [err, setErr] = useState('');
  const list = sugg.list || [];
  const inPile = list.filter((s) => (s.status || 'pending') === pile);
  const pileCount = (p) => {
    if (sugg.counts && sugg.counts[p.countKey] != null) return Number(sugg.counts[p.countKey]) || 0;
    return list.filter((s) => (s.status || 'pending') === p.id).length;
  };

  const review = async (s, status, note) => {
    setBusy(s.id); setErr('');
    try {
      await call('/api/route-tests', { op: 'review', id: s.id, status, ...(note ? { note } : {}) });
      await reload();
      return true;
    } catch (e) { setErr(e.message); return false; }
    finally { setBusy(''); }
  };
  const remove = async (s) => {
    if (!window.confirm(`Delete this suggestion from ${s.byName || s.by || 'a volunteer'}? It disappears from their list too, and this cannot be undone.`)) return;
    setBusy(s.id); setErr('');
    try {
      await call('/api/route-tests', { op: 'delete', id: s.id });
      await reload();
    } catch (e) { setErr(e.message); }
    finally { setBusy(''); }
  };

  return (
    <>
      <ActionBar>
        <ActionGroup
          label="Route tester"
          note="A suggestion is a route a volunteer re-ran with test edits — a blocked node, a disabled shaft — and the numbers before and after. Nothing here changed the map; a change that stands is made in UMapsTools. Your notes go back to the volunteer."
        >
          <a style={S.btnGhost} href="/volunteer/routes">Open Route tester ↗</a>
          <button style={S.btnGhost} onClick={reload} disabled={!!busy}>Refresh</button>
        </ActionGroup>
      </ActionBar>
      {sugg.err && <div style={S.err}>{sugg.err}</div>}
      {err && <div style={S.err}>{err}</div>}

      <div style={S.filterBar}>
        <ActionGroup label="Pile">
          {PILES.map((p) => (
            <button
              key={p.id}
              style={{ ...S.chip, ...(pile === p.id ? S.chipOn : {}) }}
              onClick={() => setPile(p.id)}
            >
              {p.label} ({pileCount(p)})
            </button>
          ))}
        </ActionGroup>
      </div>

      <div style={S.reportList}>
        {sugg.list === null && !sugg.err && <div style={S.reportEmpty}>Loading…</div>}
        {sugg.list !== null && inPile.length === 0 && (
          <div style={S.reportEmpty}>
            {pile === 'pending' ? 'Nothing waiting. Suggestions sent from the Route tester land here.' : 'Nothing in this pile.'}
          </div>
        )}
        {inPile.map((s) => (
          <SuggestionCard key={s.id} s={s} busy={busy === s.id} apiBase={apiBase} token={token} onReview={review} onDelete={remove} />
        ))}
      </div>
    </>
  );
}

function SuggestionCard({ s, busy, apiBase, token, onReview, onDelete }) {
  // An action that takes a note opens a box under the card rather than a
  // prompt(): "Needs detail" is a question to a person and deserves a sentence.
  const [act, setAct] = useState(null);  // 'needs-detail' | 'closed' | null
  const [note, setNote] = useState('');
  const status = s.status || 'pending';
  const thread = Array.isArray(s.thread) ? s.thread : [];
  const edits = Array.isArray(s.edits) ? s.edits : [];
  const original = s.original || {};
  const variants = useMemo(() => {
    const keys = new Set([...Object.keys(s.before || {}), ...Object.keys(s.after || {})]);
    return VARIANT_ORDER.filter((k) => keys.has(k)).concat([...keys].filter((k) => !VARIANT_ORDER.includes(k)));
  }, [s.before, s.after]);
  const shot = typeof s.screenshot === 'string' && s.screenshot ? s.screenshot : null;
  const testerHref = `/volunteer/routes?suggestion=${encodeURIComponent(s.id)}`;

  const send = async () => {
    const text = note.trim();
    if (act === 'needs-detail' && !text) return;
    const ok = await onReview(s, act, text);
    if (ok) { setAct(null); setNote(''); }
  };

  return (
    <div style={S.reportItem}>
      <div style={{ ...S.reportHead, ...S.ratingHead }}>
        <span style={{ ...S.ratingChip, ...S.statusChip, ...(STATUS_TONE[status] || {}) }}>{STATUS_LABEL[status] || status}</span>
        <div style={S.ratingMain}>
          <div style={S.ratingRoute}>
            {endpointText(original.start)}
            <span style={S.ratingArrow}>→</span>
            {endpointText(original.end)}
          </div>
          <div style={S.ratingMeta}>
            {[
              s.byName ? `${s.byName} (${s.by || '—'})` : (s.by || 'Unknown volunteer'),
              fmtTime(s.at),
              variantsText(original.variants),
            ].filter(Boolean).join(' · ')}
          </div>
          {s.reviewedAt && (
            <div style={S.ratingMeta}>Reviewed {fmtTime(s.reviewedAt)}{s.reviewedBy ? ` by ${s.reviewedBy}` : ''}</div>
          )}
        </div>
        <div style={S.ratingControls}>
          <a style={S.openRoute} href={testerHref}>Open in tester ↗</a>
        </div>
      </div>

      <div style={S.suggBody}>
        <div style={S.suggGrid}>
          <div style={S.noteLabel}>Edits ({edits.length})</div>
          <div>
            {edits.length === 0 && <span style={S.dim}>No edits — the route as it is.</span>}
            {edits.length > 0 && (
              <ul style={S.editList}>
                {edits.map((e, i) => <li key={i}>{editText(e)}</li>)}
              </ul>
            )}
          </div>

          <div style={S.noteLabel}>Before → after</div>
          <div>
            {variants.length === 0 && <span style={S.dim}>No numbers recorded.</span>}
            {variants.length > 0 && (
              <table style={S.metricTable}>
                <tbody>
                  {variants.map((k) => (
                    <tr key={k}>
                      <td style={S.metricVariant}>{VARIANT_LABELS[k] || k}</td>
                      <td style={S.metricCell}>{metricText(s.before?.[k])}</td>
                      <td style={S.metricArrow}>→</td>
                      <td style={S.metricCell}>{metricText(s.after?.[k])}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <div style={S.noteLabel}>Thread</div>
          <div>
            {thread.length === 0 && !s.comment && <span style={S.dim}>No comment.</span>}
            {thread.length === 0 && s.comment && <div style={S.threadText}>“{s.comment}”</div>}
            {thread.map((m, i) => (
              <div key={i} style={{ ...S.threadMsg, ...(m.role === 'operator' ? S.threadOperator : {}) }}>
                <div style={S.threadWho}>
                  {m.role === 'operator' ? 'You' : 'Volunteer'}{m.by ? ` · ${m.by}` : ''}{m.at ? ` · ${fmtTime(m.at)}` : ''}
                </div>
                <div style={S.threadText}>{m.text}</div>
              </div>
            ))}
          </div>

          {shot && (
            <>
              <div style={S.noteLabel}>Screenshot</div>
              <div><AuthImage src={shot} apiBase={apiBase} token={token} /></div>
            </>
          )}
        </div>

        <div style={S.noteActions}>
          {status !== 'needs-detail' && (
            <button style={S.btnGhost} disabled={busy} onClick={() => { setAct(act === 'needs-detail' ? null : 'needs-detail'); setNote(''); }}>
              Needs detail
            </button>
          )}
          {status !== 'closed' && (
            <button style={S.btnGhost} disabled={busy} onClick={() => { setAct(act === 'closed' ? null : 'closed'); setNote(''); }}>
              Close
            </button>
          )}
          {status !== 'pending' && (
            <button style={S.btnGhost} disabled={busy} onClick={() => onReview(s, 'pending', '')}>
              {busy ? '…' : 'Reopen'}
            </button>
          )}
          <button style={{ ...S.btnDanger, marginLeft: 'auto' }} disabled={busy} onClick={() => onDelete(s)}>Delete</button>
        </div>

        {act && (
          <div style={S.noteBlock}>
            <div style={S.noteLabel}>
              {act === 'needs-detail' ? 'What do you need from the volunteer?' : 'A note for the volunteer (optional)'}
            </div>
            <textarea
              style={S.noteBox} rows={3} value={note} onChange={(e) => setNote(e.target.value)}
              placeholder={act === 'needs-detail'
                ? 'e.g. Which door was locked — the east one on floor 2, or the stairwell?'
                : 'e.g. Fixed in UMapsTools; lands with the next snapshot.'}
            />
            <div style={S.noteActions}>
              <button
                style={{ ...S.noteSave, ...((act === 'needs-detail' && !note.trim()) || busy ? S.noteSaveOff : {}) }}
                disabled={(act === 'needs-detail' && !note.trim()) || busy}
                onClick={send}
              >
                {busy ? 'Saving…' : act === 'needs-detail' ? 'Ask for detail' : 'Close suggestion'}
              </button>
              <button style={S.btnGhost} disabled={busy} onClick={() => { setAct(null); setNote(''); }}>Cancel</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * A suggestion's screenshot.
 *
 * In production `screenshot` is an absolute Blob URL and loads directly. In
 * dev it is `/api/route-tests?part=file&id=…`, which the server gates on the
 * Bearer token — and an `<img>` cannot send a header — so that form is
 * fetched with the token, turned into an object URL, and revoked when the
 * card goes away or the source changes. Either way a load failure degrades to
 * a plain link rather than a broken-image icon.
 */
function AuthImage({ src, apiBase, token }) {
  const relative = typeof src === 'string' && src.startsWith('/api/');
  const direct = relative ? `${apiBase}${src}` : src;
  const [objectUrl, setObjectUrl] = useState('');
  const [broken, setBroken] = useState(false);

  useEffect(() => {
    setBroken(false);
    setObjectUrl('');
    if (!relative) return undefined;
    let url = '';
    let stopped = false;
    (async () => {
      try {
        const r = await fetch(direct, { headers: { Authorization: `Bearer ${token}` } });
        if (!r.ok) throw new Error(String(r.status));
        const blob = await r.blob();
        if (stopped) return;
        url = URL.createObjectURL(blob);
        setObjectUrl(url);
      } catch { if (!stopped) setBroken(true); }
    })();
    return () => {
      stopped = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [relative, direct, token]);

  const shown = relative ? objectUrl : direct;
  if (broken) {
    return <a style={S.openRoute} href={direct} target="_blank" rel="noreferrer">Open the screenshot ↗</a>;
  }
  if (!shown) return <span style={S.dim}>Loading screenshot…</span>;
  return (
    <a href={shown} target="_blank" rel="noreferrer">
      <img src={shown} alt="Screenshot from the Route tester" loading="lazy" style={S.shot} onError={() => setBroken(true)} />
    </a>
  );
}

// ── Small shared pieces (copies of AdminPage's) ─────────────────────────────

function Stat({ label, value, hint, tone }) {
  return (
    <div style={{ ...S.stat, ...(tone === 'warn' ? S.statWarn : {}) }}>
      <div style={S.statValue}>{value}</div>
      <div style={S.statLabel}>{label}</div>
      {hint && <div style={S.statHint}>{hint}</div>}
    </div>
  );
}

function ActionGroup({ label, note, children }) {
  return (
    <div style={S.group}>
      <div style={S.groupLabel}>{label}</div>
      <div style={S.groupButtons}>{children}</div>
      {note && <div style={S.groupNote}>{note}</div>}
    </div>
  );
}

const ActionBar = ({ children }) => <div style={S.actionBar}>{children}</div>;

/** The download control, same words and place as on every Dashboard tab. */
function ExportGroup({ tab, data }) {
  const [working, setWorking] = useState('');
  if (!canExport(tab)) {
    return (
      <ActionGroup label="Download" note="This page has no spreadsheet — Analytics → Download data has every table.">
        <button style={{ ...S.btnGhost, ...S.btnOff }} disabled>Not available here</button>
      </ActionGroup>
    );
  }
  const rows = exportRowCount(tab, data);
  const sheets = pageSheetCount(tab, data);
  const run = async (kind, fn) => {
    setWorking(kind);
    try { await fn(tab, data); } finally { setWorking(''); }
  };
  return (
    <ActionGroup
      label="Download"
      note={sheets > 1
        ? `This page is ${sheets} tables. The workbook keeps them on separate sheets; the CSV is the first one only.`
        : 'Excel and Sheets both open either.'}
    >
      <button style={S.btnGhost} disabled={!rows || !!working} onClick={() => run('xlsx', downloadPageXlsx)}>
        {working === 'xlsx' ? 'Collecting…' : `Download this page (${rows.toLocaleString('en-CA')} row${rows === 1 ? '' : 's'})`}
      </button>
      <button style={S.btnGhost} disabled={!rows || !!working} onClick={() => run('csv', downloadPageCsv)}>
        {working === 'csv' ? 'Collecting…' : 'CSV'}
      </button>
    </ActionGroup>
  );
}

const S = {
  err: { background: '#fce8e6', color: '#c5221f', borderRadius: 8, padding: '8px 11px', fontSize: 13, marginBottom: 12 },
  ok: { background: '#e6f4ea', color: '#137333', borderRadius: 8, padding: '8px 11px', fontSize: 13, marginBottom: 12 },
  btnGhost: { background: '#fff', color: '#202124', border: '1.5px solid #dadce0', borderRadius: 8, padding: '8px 14px', fontSize: 14, fontWeight: 700, cursor: 'pointer', textDecoration: 'none', display: 'inline-block', boxSizing: 'border-box' },
  btnDanger: { background: '#fff', color: '#c5221f', border: '1.5px solid #f3b8b4', borderRadius: 8, padding: '8px 14px', fontSize: 14, fontWeight: 700, cursor: 'pointer' },
  btnOff: { color: '#80868b', cursor: 'default' },
  smallPrimary: { background: '#1a73e8', color: '#fff', border: '1.5px solid #1a73e8', borderRadius: 8, padding: '5px 10px', fontSize: 12.5, fontWeight: 700, cursor: 'pointer' },
  smallGhost: { background: '#fff', color: '#202124', border: '1.5px solid #dadce0', borderRadius: 8, padding: '5px 10px', fontSize: 12.5, fontWeight: 700, cursor: 'pointer' },

  actionBar: { display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-start', margin: '0 0 12px' },
  group: { background: '#fff', border: '1px solid #dadce0', borderRadius: 12, padding: '9px 12px 11px', flex: '1 1 260px', minWidth: 0 },
  groupLabel: { fontSize: 11, fontWeight: 800, color: '#5f6368', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 7 },
  groupButtons: { display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' },
  groupNote: { fontSize: 12, color: '#5f6368', lineHeight: 1.45, marginTop: 8 },

  tabBar: { display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', marginBottom: 12 },
  tab: { background: '#fff', border: '1.5px solid #dadce0', borderRadius: 999, padding: '8px 16px', fontSize: 14, fontWeight: 700, cursor: 'pointer', color: '#202124' },
  tabOn: { background: '#1a73e8', borderColor: '#1a73e8', color: '#fff' },
  tabDot: { display: 'inline-block', width: 7, height: 7, borderRadius: '50%', background: '#d93025', marginLeft: 7, verticalAlign: 'middle' },
  sectionTitle: { fontSize: 12, fontWeight: 800, color: '#5f6368', textTransform: 'uppercase', letterSpacing: '0.05em', margin: '14px 0 8px' },

  stat: { background: '#fff', border: '1px solid #dadce0', borderRadius: 12, padding: '11px 15px', minWidth: 118 },
  statWarn: { borderColor: '#f3c4c1', background: '#fff8f7' },
  statValue: { fontSize: 22, fontWeight: 800, lineHeight: 1.15 },
  statLabel: { fontSize: 12.5, color: '#3c4043', fontWeight: 600, marginTop: 2 },
  statHint: { fontSize: 11.5, color: '#5f6368', marginTop: 2 },
  counts: { display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 },

  tableWrap: { background: '#fff', borderRadius: 12, overflow: 'auto', boxShadow: '0 2px 12px rgba(0,0,0,0.08)' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  th: { textAlign: 'left', padding: '10px 12px', borderBottom: '2px solid #e8eaed', color: '#5f6368', fontWeight: 700, whiteSpace: 'nowrap', position: 'sticky', top: 0, background: '#fff' },
  td: { padding: '9px 12px', borderBottom: '1px solid #f1f3f4', verticalAlign: 'top' },
  dim: { color: '#80868b' },
  mono: { fontFamily: 'ui-monospace, "SF Mono", Menlo, Consolas, monospace', fontSize: 12 },

  reportList: { display: 'flex', flexDirection: 'column', gap: 8 },
  reportEmpty: { background: '#fff', borderRadius: 12, padding: 16, fontSize: 14, color: '#5f6368' },
  reportItem: { background: '#fff', borderRadius: 12, boxShadow: '0 2px 12px rgba(0,0,0,0.08)', overflow: 'hidden' },
  // ⚠️ `boxSizing` is load-bearing: `width: 100%` plus 14px of horizontal
  // padding, with no global reset on this page, makes the row 28px wider than
  // the card it sits in, and the card is `overflow: hidden`.
  reportHead: { display: 'flex', alignItems: 'center', gap: 10, width: '100%', boxSizing: 'border-box', padding: '11px 14px', border: 'none', background: 'none', cursor: 'pointer', textAlign: 'left', fontSize: 13 },
  ratingChip: { borderRadius: 999, padding: '3px 0', width: 52, textAlign: 'center', fontWeight: 800, textTransform: 'uppercase', fontSize: 11, letterSpacing: 0.4, whiteSpace: 'nowrap', flexShrink: 0 },
  // "needs detail" is longer than any verdict; the suggestion chips widen to it.
  statusChip: { width: 92 },
  filterBar: { display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 12, alignItems: 'flex-start' },
  ratingHead: { cursor: 'default', alignItems: 'flex-start' },
  ratingMain: { flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 3 },
  ratingRoute: { color: '#202124', fontWeight: 600, lineHeight: 1.35, overflowWrap: 'anywhere' },
  ratingArrow: { color: '#9aa0a6', padding: '0 6px' },
  ratingMeta: { color: '#5f6368', fontSize: 12, lineHeight: 1.35, overflowWrap: 'anywhere' },
  ratingControls: { display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 },
  chip: { background: '#fff', border: '1px solid #dadce0', borderRadius: 999, padding: '5px 12px', fontSize: 13, fontWeight: 600, color: '#3c4043', cursor: 'pointer', whiteSpace: 'nowrap' },
  chipOn: { background: '#1a73e8', border: '1px solid #1a73e8', color: '#fff' },
  statusSelect: { border: '1px solid #dadce0', borderRadius: 999, padding: '4px 8px', width: 118, fontSize: 12, fontWeight: 700, flexShrink: 0, textAlign: 'center', boxSizing: 'border-box' },
  openRoute: { color: '#1a73e8', fontWeight: 700, fontSize: 12, textDecoration: 'none', whiteSpace: 'nowrap', flexShrink: 0, border: '1px solid #d2e3fc', borderRadius: 999, padding: '4px 10px', background: '#f8fbff' },
  openRouteOff: { color: '#9aa0a6', fontSize: 12, whiteSpace: 'nowrap', flexShrink: 0, padding: '4px 10px' },
  ratingSaid: { fontSize: 14, color: '#3c4043', whiteSpace: 'pre-wrap', lineHeight: 1.4, padding: '0 14px 12px 44px' },
  volNote: { margin: '0 14px 12px 44px', padding: '8px 10px', background: '#fef7e0', border: '1px solid #fde293', borderRadius: 8 },
  volNoteText: { fontSize: 13, color: '#3c4043', whiteSpace: 'pre-wrap', lineHeight: 1.45 },
  noteLabel: { fontSize: 11.5, fontWeight: 800, letterSpacing: 0.5, textTransform: 'uppercase', color: '#5f6368', marginBottom: 6 },
  noteBox: { width: '100%', boxSizing: 'border-box', border: '1px solid #dadce0', borderRadius: 8, padding: '8px 10px', font: 'inherit', fontSize: 13, lineHeight: 1.45, resize: 'vertical' },
  noteSave: { background: '#1a73e8', color: '#fff', border: '1.5px solid #1a73e8', borderRadius: 8, padding: '8px 14px', fontSize: 14, fontWeight: 700, cursor: 'pointer' },
  noteSaveOff: { background: '#f1f3f4', color: '#9aa0a6', border: '1.5px solid #f1f3f4', cursor: 'default' },
  noteActions: { display: 'flex', alignItems: 'center', gap: 8, marginTop: 8, flexWrap: 'wrap' },
  noteBlock: { marginTop: 12, paddingTop: 12, borderTop: '1px solid #f1f3f4' },

  // ── Suggestion cards ──────────────────────────────────────────────────────
  suggBody: { borderTop: '1px solid #f1f3f4', padding: '12px 14px' },
  suggGrid: { display: 'grid', gridTemplateColumns: 'max-content 1fr', gap: '8px 14px', fontSize: 13, color: '#3c4043', alignItems: 'start' },
  editList: { margin: 0, paddingLeft: 18, lineHeight: 1.5 },
  metricTable: { borderCollapse: 'collapse', fontSize: 13 },
  metricVariant: { padding: '2px 10px 2px 0', fontWeight: 700, whiteSpace: 'nowrap', color: '#202124' },
  metricCell: { padding: '2px 0', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' },
  metricArrow: { padding: '2px 8px', color: '#9aa0a6' },
  threadMsg: { padding: '6px 10px', background: '#f8f9fa', border: '1px solid #e8eaed', borderRadius: 8, marginBottom: 6 },
  threadOperator: { background: '#fef7e0', borderColor: '#fde293' },
  threadWho: { fontSize: 11.5, fontWeight: 700, color: '#5f6368', marginBottom: 2 },
  threadText: { fontSize: 13, color: '#202124', whiteSpace: 'pre-wrap', lineHeight: 1.45 },
  shot: { display: 'block', maxWidth: '100%', maxHeight: 420, borderRadius: 8, border: '1px solid #e8eaed', background: '#f1f3f4' },
};
